import {
  LoadingManager,
  TextureLoader,
  Object3D,
  Group,
  Texture,
  Mesh,
  MeshStandardMaterial,
} from "three";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { GLTFLoader, GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { Font, FontLoader } from "three/examples/jsm/loaders/FontLoader.js";

import concrete_color from ".././textures/concrete_tile_color.jpg";
import concrete_normal from ".././textures/concrete_tile_normal.jpg";
import concrete_rough from ".././textures/concrete_tile_rough.jpg";

import water_norm1 from ".././textures/Water_1_M_Normal.jpg";
import water_norm2 from ".././textures/Water_2_M_Normal.jpg";
import water_flow from ".././textures/Water_1_M_Flow.jpg";

import wood_color from ".././textures/poolwood_abledo.jpg";
import wood_norm from ".././textures/poolwood_norm.png";
import wood_rough from ".././textures/poolwood_spec.png";

import grass_color from ".././textures/grass_BaseColor.jpg";
import grass_normal from ".././textures/grass_Normal.jpg";
import grass_rough from ".././textures/grass_Roughness.jpg";
import grass_ao from ".././textures/grass_AO.png";



import mud_leaves_color from ".././textures/mud_leaves_color.jpg";
import mud_leaves_normal from ".././textures/mud_leaves_normal.jpg";
import mud_leaves_rough from ".././textures/mud_leaves_rough.jpg";

import plant1_color from ".././textures/plant1_color.jpg";
import plant1_normal from ".././textures/plant1_normal.jpg";
import plant1_rough from ".././textures/plant1_rough.jpg";
import plant2_color from ".././textures/plant2_color.jpg";
import plant2_normal from ".././textures/plant2_normal.jpg";
import plant2_rough from ".././textures/plant2_rough.jpg";

// hdris

import skyDay from "url:.././textures/sky6.hdr";
import skyNight from "url:.././textures/sky-night.hdr";

// models

import plant1 from "url:.././models/plant1.fbx";
import plant2 from "url:.././models/plant2.fbx";
import plant6 from "url:.././models/plant6.fbx";

import relax from "url:.././models/relax.fbx";
import relax2 from "url:.././models/relax2.fbx";

import spot from "url:.././models/spot.fbx";
import skimmer from "url:.././models/skimmer.fbx";

import sofa from "url:.././models/sofa.fbx";
import sofa_single from "url:.././models/sofa_single.fbx";

import pot1 from "url:.././models/pot1.fbx";
import grass from "url:.././models/grass2.fbx";
import parasol from "url:.././models/parasol.fbx";

// Caustics

import causticMap from ".././textures/caustics.png";

export default class AssetManager {
  private static instance: AssetManager;
  private assetsCache: Map<string, Object3D | Texture | Font>;
  private loadingManager: LoadingManager;

  private loadComplete: Promise<void>;
  private resolveLoadComplete: () => void;

  private constructor() {
    this.assetsCache = new Map();
    this.loadingManager = new LoadingManager();

    this.loadComplete = new Promise<void>((resolve) => {
      this.resolveLoadComplete = resolve;
    });
    this.initLoadingManager();
  }

  public static getInstance(): AssetManager {
    if (!AssetManager.instance) {
      AssetManager.instance = new AssetManager();
    }
    return AssetManager.instance;
  }

  private initLoadingManager() {
    // Show loading screen here
    const loadingElement = document.getElementById("loading");
    if (loadingElement) {
      loadingElement.classList.remove("hidden");
    }

    this.loadingManager.onLoad = () => {
      // Hide loading screen here
      if (loadingElement) {
        loadingElement.classList.add("hidden");
      }
      console.log("All assets loaded");
      this.resolveLoadComplete();
    };

    this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
      
    };
  }

  public whenLoadComplete(): Promise<void> {
    return this.loadComplete;
  }

  private parseFbxMeshMaterials(fbx: Group): void {

    fbx.castShadow = true;
      fbx.receiveShadow = true;

    fbx.children.forEach((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
  
        if (Array.isArray(child.material)) {
          for (let i = 0; i < child.material.length; i++) {
            const difMap = child.material[i].map;
            child.material[i] = new MeshStandardMaterial({
              map: difMap,
              side:2,
              
            });
          }
        } else {
          const difMap = child.material.map;
          const normalMap = child.material.normalMap;
          child.material = new MeshStandardMaterial({
            map: difMap,
            normalMap: normalMap,
            side: 2,
          });
        }
      }
    });
  }

  public loadAssets() {
    const textureLoader = new TextureLoader(this.loadingManager);
    const fbxLoader = new FBXLoader(this.loadingManager);
    const rgbeLoader = new RGBELoader(this.loadingManager);
    const gltfLoader = new GLTFLoader(this.loadingManager);
    const localAssets = this.assetsCache;


    rgbeLoader.load(skyDay, function (texture) {
      localAssets.set("sky_day", texture);
    });

    rgbeLoader.load(skyNight, function (texture) {
      localAssets.set("sky_night", texture);
    });

    textureLoader.load(plant1_color, (texture) => {
      this.assetsCache.set("plant1_color", texture);
    });

    textureLoader.load(plant1_normal, (texture) => {
      this.assetsCache.set("plant1_normal", texture);
    });

    textureLoader.load(plant1_rough, (texture) => {
      this.assetsCache.set("plant1_rough", texture);
    });

    textureLoader.load(plant2_color, (texture) => {
      this.assetsCache.set("plant2_color", texture);
    });

    textureLoader.load(plant2_normal, (texture) => {
      this.assetsCache.set("plant2_normal", texture);
    });

    textureLoader.load(plant2_rough, (texture) => {
      this.assetsCache.set("plant2_rough", texture);
    });

    textureLoader.load(mud_leaves_color, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("mud_leaves_color", texture);
    });

    textureLoader.load(mud_leaves_normal, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("mud_leaves_normal", texture);
    });

    textureLoader.load(mud_leaves_rough, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("mud_leaves_rough", texture);
    });

    textureLoader.load(concrete_color, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("concrete_color", texture);
    });

    textureLoader.load(concrete_normal, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("concrete_normal", texture);
    });

    textureLoader.load(concrete_rough, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("concrete_rough", texture);
    });

    textureLoader.load(causticMap, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("causticMap", texture);
    });

    textureLoader.load(water_norm1, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("water_norm1", texture);
    });

    textureLoader.load(water_norm2, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("water_norm2", texture);
    });

    textureLoader.load(water_flow, (texture) => {
      this.assetsCache.set("water_flow", texture);
    });

    textureLoader.load(wood_color, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("wood_color", texture);
    });

    textureLoader.load(wood_norm, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("wood_normal", texture);
    });

    textureLoader.load(wood_rough, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("wood_rough", texture);
    });

    textureLoader.load(grass_color, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("grass_color", texture);
    });

    textureLoader.load(grass_normal, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("grass_normal", texture);
    });

    textureLoader.load(grass_rough, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("grass_rough", texture);
    });

    textureLoader.load(grass_ao, (texture) => {
      texture.wrapS = texture.wrapT = 1000;
      this.assetsCache.set("grass_ao", texture);
    });



    /////// FBX MODELS ///////

    fbxLoader.load(grass, (fbx: Group) => {
      this.parseFbxMeshMaterials(fbx);
      this.assetsCache.set("grass", fbx);
    });

    fbxLoader.load(parasol, (fbx: Group) => {
      this.parseFbxMeshMaterials(fbx);
      this.assetsCache.set("parasol", fbx);
    });


    fbxLoader.load(pot1, (fbx: Group) => {
      this.parseFbxMeshMaterials(fbx);
      this.assetsCache.set("pot1", fbx);
    });

    fbxLoader.load(plant1, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;
      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      this.assetsCache.set("plant1", fbx);
    });

    fbxLoader.load(plant2, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;
      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      this.assetsCache.set("plant2", fbx);
    });

    fbxLoader.load(plant6, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;
      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          const difMap = child.material.map;
          child.renderOrder = 3;
          child.material = new MeshStandardMaterial({
            map: difMap,
            transparent: true,
            side: 2,
          });
        }
      });
      this.assetsCache.set("plant6", fbx);
    });

    fbxLoader.load(relax, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;

      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.geometry.computeVertexNormals();
          child.castShadow = true;
          child.receiveShadow = true;
          const difMap = child.material.map;

          child.material = new MeshStandardMaterial({
            map: difMap,
          });
        }
      });
      this.assetsCache.set("relax", fbx);
    });

    fbxLoader.load(relax2, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;

      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.geometry.computeVertexNormals();
          child.castShadow = true;
          child.receiveShadow = true;
          const difMap = child.material.map;
          const normalMap = child.material.normalMap;

          child.material = new MeshStandardMaterial({
            map: difMap,
            normalMap: normalMap,
          });
        }
      });
      this.assetsCache.set("relax2", fbx);
    });

    fbxLoader.load(spot, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;

      this.parseFbxMeshMaterials(fbx);
      
      this.assetsCache.set("spot", fbx);
    });

    fbxLoader.load(skimmer, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;

      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.geometry.computeVertexNormals();
          child.castShadow = true;
          child.receiveShadow = true;
          const difMap = child.material.map;
          child.material = new MeshStandardMaterial({
            map: difMap,
          });
        }
      });
      this.assetsCache.set("skimmer", fbx);
    });

    fbxLoader.load(sofa, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;

      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.geometry.computeVertexNormals();
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      this.assetsCache.set("sofa", fbx);
    });

    fbxLoader.load(sofa_single, (fbx: Group) => {
      fbx.castShadow = true;
      fbx.receiveShadow = true;

      // loop through all childs
      fbx.traverse(function (child) {
        if (child instanceof Mesh) {
          child.geometry.computeVertexNormals();
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      this.assetsCache.set("sofa_single", fbx);
    });

    // Load more assets here
  }

  public getAsset(key: string): Object3D | Texture | Font | undefined {
    return this.assetsCache.get(key);
  }
}
